import { ReactElement } from "react";
import Markdown from "react-markdown";
import {
  MediaObject,
  MediaFigure,
  MediaBody,
} from "@twilio-paste/core/media-object";
import { Avatar } from "@twilio-paste/core/avatar";
import { Box } from "@twilio-paste/core/box";
import { Anchor } from "@twilio-paste/core/anchor";
import { ArtificialIntelligenceIcon } from "@twilio-paste/icons/esm/ArtificialIntelligenceIcon";
import { CodeBlock } from "@twilio-paste/core/code-block";
import { InlineCode } from "@twilio-paste/core/inline-code";
import { SnippetLanguages } from "@twilio-paste/core/syntax-highlighter-library";

import {
  MessageBlockRole,
  MessageCompletionStatus,
  dataTestIds,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";
import { useWiseOwlContext } from "../../../hooks";
import { constants, types } from "../definitions";
import { MessageSkeletonLoader, RetryRegenerateRenderer } from ".";
import Feedback from "../../feedback";

const MessageMarkdown = ({ markdown }: { markdown: string }): ReactElement => {
  return (
    <Markdown
      components={{
        code({ children, className }) {
          const match = /language-(\w+)/.exec(className || "");
          const language = match ? match[1] : "";
          const code = children?.toString();

          if (!code) {
            /**
             * Returning 'null' is expected for react-markdown if there's no value to return.
             * Please do not return undefined here
             */
            return null;
          }

          const isInLine = !code.includes("\n");
          if (isInLine) {
            return <InlineCode>{code}</InlineCode>;
          }

          return (
            <CodeBlock
              language={language as SnippetLanguages}
              code={code}
              maxLines={10}
            />
          );
        },
        a({ children, href }) {
          if (!href) {
            return null;
          }
          return (
            <Anchor href={href} showExternal>
              {children?.toString() || ""}
            </Anchor>
          );
        },
      }}
    >
      {markdown}
    </Markdown>
  );
};

// Renders skeleton loader while markdown length is below a certain threshold
const StreamedMessageMarkdown = ({
  markdown,
  loadThreshold,
}: {
  markdown: string;
  loadThreshold: number;
}): ReactElement => {
  if (markdown.length < loadThreshold) {
    return <MessageSkeletonLoader />;
  }

  return <MessageMarkdown markdown={markdown} />;
};

const MessageRenderer = ({
  block,
}: types.MessageRendererProps): ReactElement => {
  const { author } = useWiseOwlContext();
  const { role, message, id, isComplete, completionStatus } = block.values;

  const isMessageError = completionStatus === MessageCompletionStatus.Error;
  const isAssistantMessageStreaming =
    role === MessageBlockRole.Assistant && !isComplete && !isMessageError;
  const isAssistantMessageComplete =
    role === MessageBlockRole.Assistant && isComplete;

  return (
    <MediaObject verticalAlign="top">
      <MediaFigure
        spacing="space50"
        data-testid={
          role === MessageBlockRole.Assistant
            ? `${dataTestIds.wiseOwlChat.helpAssistantMediaObject}`
            : `${dataTestIds.wiseOwlChat.userMediaObject}`
        }
      >
        <Box paddingTop="space40">
          <Avatar
            size="sizeIcon50"
            {...(role === MessageBlockRole.User
              ? { name: author }
              : {
                  name: constants.MESSAGE_AI_NAME,
                  icon: ArtificialIntelligenceIcon,
                })}
            color={role === "human" ? "decorative20" : "decorative40"}
          />
        </Box>
      </MediaFigure>
      <MediaBody>
        {/* Markdown */}
        {isAssistantMessageStreaming ? (
          <StreamedMessageMarkdown markdown={message} loadThreshold={300} />
        ) : (
          <>
            <MessageMarkdown markdown={message} />
            {/* Retry Message */}
            <RetryRegenerateRenderer block={block} />
          </>
        )}

        {/* (Assistant) Feedback form */}
        {isAssistantMessageComplete && <Feedback messageId={id} />}
      </MediaBody>
    </MediaObject>
  );
};

export default MessageRenderer;
